import React, { useEffect } from 'react';
import { Routes, Route, Navigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { verify } from "./redux";
import Navbar from './Main/Components/Navbar';
import Footer from './Main/Components/Footer';
import Home from './Main/Home';
import Student from './Main/Student';
import Teacher from './Main/Teacher';
import Admin from './Main/Admin';
import About from './Main/About';
import FAQPage from './Main/FAQ';
import Contact from './Main/Contact';
import Login from './Main/Login';
import Profile from './Main/Profile';
import ManageClasses from './Main/Teacher/ManageClasses';
import ManageArchivedClasses from './Main/Teacher/ManageArchivedClasses';
import ManageStudents from './Main/Teacher/ManageStudents';
import ForgotPassword from './Main/ForgotPassword';
import ResetPassword from './Main/ResetPassword';
import EvaluationForm from './Main/Student/Evaluation/New';
import SubmittedEvaluations from './Main/Student/SubmittedEvaluations';
import EditGrading from './Main/Teacher/Grading/Edit';
import EditEvaluationForm from './Main/Student/Evaluation/Edit';
import ExtraEvaluationForm from './Main/Student/Evaluation/Extra';
import ReviewEvaluations from './Main/Student/ReviewEvaluations';
import TeacherReviewEvaluations from './Main/Teacher/ReviewEvaluations';
import TeacherSprintGrades from './Main/Teacher/SprintGrades';
import NewGrading from './Main/Teacher/Grading/New';
import AssignmentForm from './Main/Teacher/Assignment/New';
import EditStudentForm from './Main/Teacher/ManageStudents/EditStudentForm';
import StudentOverview from './Main/Teacher/ManageStudents/StudentOverview';
import EditAssignmentForm from './Main/Teacher/Assignment/Edit';
import SprintForm from './Main/Teacher/Sprint';
import RespondedFeedback from './Main/Admin/RespondedFeedback';
import Metrics from './Main/Admin/Metrics';
import SchoolEdit from './Main/Admin/SchoolEdit';
import StudentSignupForm from './Main/StudentSignupForm';
import TeacherSignupForm from './Main/TeacherSignupForm';
import ProtectedStudentRoute from "./Protected/StudentRoute";
import ProtectedTeacherRoute from "./Protected/TeacherRoute";
import ProtectedAdminRoute from "./Protected/AdminRoute";
import ProtectedRoute from "./Protected/Route";
import ManageUser from './Main/Admin/ManageUser';

function dashboardFor(user) {
    if (user.type === 'student') return '/student';
    if (user.type === 'teacher') return '/teacher';
    return '/admin';
}

function App() {
    const dispatch = useDispatch();
    const { isAuthenticated, user } = useSelector(state => state.root);

    useEffect(() => {
        dispatch(verify());
    }, []);

    if (isAuthenticated === null) {
        return (
            <div>
                <Navbar />
                <div className="dashboard">
                    <div className="loading">
                        Loading...
                    </div>
                </div>
                <Footer />
            </div>
        );
    }

    // Redirect authenticated users away from public-only pages
    const authRedirect = isAuthenticated
        ? <Navigate to={dashboardFor(user)} replace />
        : null;

    return (
        <div>
            <Navbar />
            <Routes>
                {/* Public-only routes — redirect authenticated users to their dashboard */}
                <Route path="/" element={isAuthenticated ? <Navigate to={dashboardFor(user)} replace /> : <Home />} />
                <Route path="/login" element={authRedirect || <Login />} />
                <Route path="/teacher/signup" element={authRedirect || <TeacherSignupForm />} />
                <Route path="/student/signup" element={authRedirect || <StudentSignupForm />} />
                <Route path="/forgot" element={authRedirect || <ForgotPassword />} />
                {/* Splat: the emailed link is /reset/<token>, and Router v6 matches paths exactly */}
                <Route path="/reset/*" element={authRedirect || <ResetPassword />} />

                {/* Open routes */}
                <Route path="/about" element={<About />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/faq" element={<FAQPage />} />

                {/* Any authenticated user */}
                <Route element={<ProtectedRoute />}>
                    <Route path="/profile" element={<Profile />} />
                </Route>

                {/* Teacher-only routes */}
                <Route element={<ProtectedTeacherRoute />}>
                    <Route path="/student/overview" element={<StudentOverview />} />
                    <Route path="/student/edit" element={<EditStudentForm />} />
                    <Route path="/student/evaluation/edit" element={<EditEvaluationForm />} />
                    <Route path="/teacher/graded" element={<EditGrading />} />
                    <Route path="/teacher/grading" element={<NewGrading />} />
                    <Route path="/teacher/reviews" element={<TeacherReviewEvaluations />} />
                    <Route path="/teacher/grades" element={<TeacherSprintGrades />} />
                    <Route path="/teacher/*" element={<Teacher />} />
                    <Route path="/sprint/new" element={<SprintForm />} />
                    <Route path="/assignment/new" element={<AssignmentForm />} />
                    <Route path="/assignment/edit" element={<EditAssignmentForm />} />
                    <Route path="/manage/classes/archived" element={<ManageArchivedClasses />} />
                    <Route path="/manage/classes" element={<ManageClasses />} />
                    <Route path="/manage/students" element={<ManageStudents />} />
                </Route>

                {/* Student-only routes */}
                <Route element={<ProtectedStudentRoute />}>
                    <Route path="/student/submitted" element={<SubmittedEvaluations />} />
                    <Route path="/student/reviews" element={<ReviewEvaluations />} />
                    <Route path="/student/*" element={<Student />} />
                    <Route path="/evaluation/new" element={<EvaluationForm />} />
                    <Route path="/evaluation/edit" element={<EditEvaluationForm />} />
                    <Route path="/evaluation/extra" element={<ExtraEvaluationForm />} />
                </Route>

                {/* Admin-only routes */}
                <Route element={<ProtectedAdminRoute />}>
                    <Route path="/admin/*" element={<Admin />} />
                    <Route path="/school/edit" element={<SchoolEdit />} />
                    <Route path="/responded" element={<RespondedFeedback />} />
                    <Route path="/metrics" element={<Metrics />} />
                    <Route path="/user/management" element={<ManageUser />} />
                </Route>
            </Routes>
            <Footer />
        </div>
    );
}

export default App;
